import React from "react";
import { Field } from "formik";
import PropTypes from "prop-types";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { autoCompleteInputStyles } from "..";
import _ from "lodash";
import { fetchAirportAutocomplete, updateAirportScore } from "../../../../api";
import AsyncSelect from "react-select/async";
import jssVariables from "../../../../jss_setup";

async function airportSearch({ query }) {
  var data = await fetchAirportAutocomplete({ name: _.trim(query) });

  const airports = (data?.airports ?? [])
    .filter((i, idx) => idx < 10)
    .map((airport) => {
      const label = ` ${airport.iata} - ${_.startCase(
        _.toLower(airport.name)
      )} - ${airport.country_code}`;
      return { ...airport, label, value: label, location_type: "airport" };
    });

  return airports;
}

function promiseOptions(inputValue) {
  if (inputValue.length < 3) {
    return null;
  }
  return new Promise((resolve) => {
    const promises = [airportSearch({ query: inputValue })];
    resolve(
      Promise.all(promises).then(([airports, hotels]) => [...(airports || [])])
    );
  });
}
const debouncedSearch = _.debounce((query, callback) => {
  if (query.length < 3) {
    callback([]);
    return;
  }

  promiseOptions(query).then((result) => callback(result));
}, 300);

function updateScore(iata) {
  if (iata) updateAirportScore({ iata });
}

const selectorStyles = (props) => {
  return {
    placeholder: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      ...(props?.customStyles?.placeholder ?? {}),
    }),
    singleValue: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
      ...(props?.customStyles?.singleValue ?? {}),
    }),
    control: (base) => ({
      ...base,
      borderRadius: "5px",
      background: props.disabled ? "lightgray" : "white",
      ...(props?.customStyles?.control ?? {}),
    }),
    input: (base) => ({
      ...base,
      height: "2.4rem",
      alignContent: "center",
      color: jssVariables.colors.text.dark,
      ...(props?.customStyles?.input ?? {}),
    }),
    indicatorsContainer: () => ({
      display: "none",
      ...(props?.customStyles?.indicatorsContainer ?? {}),
    }),
  };
};
export const AirportAutocomplete = ({
  placeholder,
  onSelect,
  disabled,
  defaultValue,
  customStyles,
}) => {
  var commonProps = {
    isDisabled: disabled,
    isClearable: true,

    defaultOptions: true,
    blurInputOnSelect: true,
    loadOptions: (query, callback) => debouncedSearch(query, callback),
    onChange: (item) => {
      if (!item) return;
      updateScore(item.iata);
      onSelect({ code: [`${item.name}___${item.id}`], meta: item });
    },
    placeholder,
  };
  if (!_.isEmpty(defaultValue)) {
    commonProps = { ...commonProps, value: defaultValue };
  }
  return (
    <AsyncSelect
      {...commonProps}
      menuPlacement="top"
      styles={selectorStyles({ disabled, customStyles })}
    />
  );
};
AirportAutocomplete.defaultProps = {
  placeholder: "",
  defaultValue: {},
  disabled: false,
};
AirportAutocomplete.propTypes = {
  placeholder: PropTypes.string.isRequired,
  defaultValue: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  customStyles: PropTypes.object,
};

const AirportAutoCompleteInputComponent = ({
  field,
  form: { touched, errors, setFieldValue },
  ...props
}) => {
  const { withErrors } = props;

  return (
    <React.Fragment>
      <AirportAutocomplete
        onSelect={(airport) => {
          setFieldValue(field.name, {
            [`${field.name}_meta`]: [airport.meta],
          });
        }}
        {...props}
      />
      {withErrors && touched[field.name] && errors[field.name] && (
        <div className={props.styles.errorMessage}>{errors[field.name]}</div>
      )}
    </React.Fragment>
  );
};
AirportAutoCompleteInputComponent.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  withErrors: PropTypes.bool,
  styles: PropTypes.object,
};

export const AirportAutoCompleteField = ({
  id,
  name,
  placeholder,
  label,
  disabled,
  defaultValue,
  withErrors = true,
  customStyles,
}) => {
  const classes = autoCompleteInputStyles({
    customStyles,
    hasLabel: Boolean(label),
  });

  return (
    <div className={classes.inputGroup} id={id}>
      <label className={classes.label}>{label}</label>
      <div className={classes.inputGroupBody}>
        <Field
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          withErrors={withErrors}
          defaultValue={defaultValue}
          customStyles={customStyles}
          component={AirportAutoCompleteInputComponent}
        />
      </div>
    </div>
  );
};
AirportAutoCompleteField.defaultProps = {
  name: "",
  label: "",
  types: [],
  placeholder: "",
  defaultValue: {},
  customStyles: {},
  id: "",
};
AirportAutoCompleteField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  types: PropTypes.array,
  disabled: PropTypes.bool,
  withErrors: PropTypes.bool,
  defaultValue: PropTypes.object,
  placeholder: PropTypes.string,
  customStyles: PropTypes.object,
  id: PropTypes.string,
};
